import { formatISO } from 'date-fns';
import { IResult as UAParserResult } from 'ua-parser-js';
import { User } from 'types';

export function commaify(num: string | number): string {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

export function capitalize(str: string) {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

export function formatCSVFilename(prefix: string, startDate: Date, endDate: Date) {
  return `${prefix}-${formatISO(startDate, {
    representation: 'date',
  })}-to-${formatISO(endDate, { representation: 'date' })}`;
}

export const formatBrowser = ({ browser }: UAParserResult) => {
  if (!browser.name) {
    return 'Unknown';
  }
  if (!browser.version) {
    return browser.name;
  }
  const v = formatMajorMinorVersion(browser.version);
  return `${browser.name} (${v})`;
};

export const formatDevice = ({ device, os }: UAParserResult) => {
  if (!os.name) {
    return 'Unknown';
  }
  if (!os.version) {
    return os.name;
  }
  const v = formatMajorMinorVersion(os.version);
  if (!device.model) {
    return `${os.name} (${v})`;
  }
  return `${device.model} (${os.name} ${v})`;
};

export const formatMajorMinorVersion = (v: string) => {
  return v.split('.').slice(0, 2).join('.');
};

/**
 * Given a time in seconds, return H:MM:SS or M:SS if under an hour.
 * E.g. 1:23:45, 23:45, 2:35, 0:35, or 0:05
 */
export const formatHoursMinutesSeconds = (time: number) => {
  const h = Math.floor(time / 3600).toString();
  const m = Math.floor((time % 3600) / 60).toString();
  const s = Math.round(time % 60).toString();
  const combo = h === '0' ? [m, s] : [h, m, s];
  return combo.map((v, i) => (i === 0 ? v : v.padStart(2, '0'))).join(':');
};

export const formatMachineNameToLabel = (name: string) => {
  // Replace underscores, dashes, dots with spaces
  name = name.toLowerCase().replace(/[._-]/, ' ').replace('-', ' ');
  // Turn duplicate spaces into single space
  name = name.replace(/\s\s+/g, ' ');
  // Capitalize first letter in each word
  return name
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const toSnakeCase = (str: string): string => {
  return str
    .replace(/[\w]([A-Z])/g, function (m) {
      return m[0] + '_' + m[1];
    })
    .replace(/[\s]+/g, '_')
    .toLowerCase();
};

export const toCamelCase = (str: string): string => {
  return str
    .replace(/[\s_]+/g, ' ')
    .replace(/(?:^\w|[A-Z]|\b\w)/g, function (letter, index) {
      return index === 0 ? letter.toLowerCase() : letter.toUpperCase();
    })
    .replace(/\s+/g, '');
};

export const camelCaseToTitleCase = (str: string) => {
  return str
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, match => match.toUpperCase());
};

export function snakeCaseKeys<T extends Record<string, any>>(
  obj: T,
): Record<string, any> {
  const newObj: Record<string, any> = {};

  Object.keys(obj).forEach(key => {
    newObj[toSnakeCase(key)] = obj[key];
  });

  return newObj;
}

export function camelCaseKeys<T extends Record<string, any>>(
  obj: T,
): Record<string, any> {
  const newObj: Record<string, any> = {};

  Object.keys(obj).forEach(key => {
    newObj[toCamelCase(key)] = obj[key];
  });

  return newObj;
}

export const formatNames = (users: User[]) => {
  const names = users.map(u => u.displayName);
  if (names.length === 0) {
    return '';
  } else if (names.length === 1) {
    return names[0];
  } else if (names.length === 2) {
    return names.join(' and ');
  } else {
    const last = names.pop();
    return names.join(', ') + ', and ' + last;
  }
};
